import type { AxiosError } from 'axios';
import { DEFAULT_RESQUEST_ERROR_MESSAGE } from 'helpers/constants';
import api from './api';
import type { User, AccessGroup, Role } from './auth';

export type { User, AccessGroup, Role }; // re-export so other modules can still import from services/user if they used to

type objError = {
  data: null;
  message: string;
  status: string;
};

export type accessGroupCreate = {
  id: number;
  profile_id: number;
  role_id: number;
};

export type userDataCreate = {
  name: string;
  email: string;
  access_groups?: accessGroupCreate[];
};

export type userTutorial = {
  tutorial: {
    financial: boolean;
  };
};

export type tableRequest = {
  perPage: number;
  page: number;
  search?: string;
  order?: string;
  direction?: 'asc' | 'desc';
};

export type accessGroupDataCreate = {
  name: string;
  display_name: string;
  dashboard_settings?: {
    dashboards?: {
      org_id: number;
      base_url: string;
      legal?: string;
      logistic?: string;
      financial?: string;
    };
  };
  clients?: number[];
};

// Keep Role type usage from auth (we re-exported it above if needed)

export function saveNewPassword(
  data: unknown,
  callback: (user: User) => void,
  handleError: (errorMsg: string) => void
) {
  api
    .put('/user/password', data)
    .then(result => {
      const { data } = result;

      callback(data);
    })
    .catch((error: AxiosError<objError>) => {
      const errorData = error?.response?.data;
      handleError(errorData?.message || DEFAULT_RESQUEST_ERROR_MESSAGE);
    });
}

export function createUser(
  data: userDataCreate,
  callback: (user: User) => void,
  handleError: (errorMsg: string) => void
) {
  api
    .post(`users`, data)
    .then(result => {
      const { data } = result;

      callback(data);
    })
    .catch((error: AxiosError<objError>) => {
      const errorData = error?.response?.data;
      handleError(errorData?.message || DEFAULT_RESQUEST_ERROR_MESSAGE);
    });
}

export function postFirstAccessCheck(data: userTutorial) {
  return api.patch(`/user/settings/tutorial`, data).then(result => {
    return result;
  });
}

export function getAccessGroups(callback: (data: AccessGroup[], total: number) => void, request: tableRequest): void {
  const { perPage, page, search, order, direction } = request;

  const searchParam = search ? `&searchParam=${encodeURIComponent(search)}` : '';
  const orderParam = order ? `&order=${encodeURIComponent(order)}` : '';
  const directionParam = direction ? `&direction=${encodeURIComponent(direction)}` : '';

  api
    .get<{
      data: AccessGroup[];
      total: number;
    }>(`/access_groups?per_page=${perPage}&page=${page}${searchParam}${orderParam}${directionParam}`)
    .then(result => {
      const { data, total } = result;
      callback(data, total);
    })
    .catch((error: AxiosError<objError>) => {
      const msg = error?.response?.data?.message || DEFAULT_RESQUEST_ERROR_MESSAGE;
      console.error('Error fetching access groups:', msg);
    });
}

export function createAccessGroup(
  data: any,
  callback: (created: { id: number; [k: string]: any }) => void,
  handleError: (errorMsg: string) => void
) {
  api
    .post(`/access_groups`, data)
    .then(result => {
      const { data } = result; // espera que a API retorne o objeto criado
      callback(data);
    })
    .catch((error: AxiosError<objError>) => {
      const errorData = error?.response?.data;
      handleError(errorData?.message || DEFAULT_RESQUEST_ERROR_MESSAGE);
    });
}

export function getUsers(
  callback: (users: User[], total: number) => void,
  request: { perPage: number; page: number; search?: string }
): void {
  const { perPage, page, search } = request;
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';

  api
    .get<{ data: User[]; total: number }>(`/users?per_page=${perPage}&page=${page}${searchParam}`)
    .then(result => {
      const { data, total } = result;
      callback(data || [], total || 0);
    })
    .catch((error: AxiosError<objError>) => {
      const msg = error?.response?.data?.message || DEFAULT_RESQUEST_ERROR_MESSAGE;
      console.error('Error fetching users:', msg);
    });
}

export function assignAccessGroupsToUser(
  accessGroupId: number,
  userIds: number[],
  callback: () => void,
  handleError: (msg: string) => void
): void {
  Promise.all(userIds.map(uid => api.post(`/user/${uid}/access_groups/add`, { access_groups: [accessGroupId] })))
    .then(() => callback())
    .catch((error: AxiosError<objError>) => {
      const msg = error?.response?.data?.message || DEFAULT_RESQUEST_ERROR_MESSAGE;
      handleError(msg);
    });
}

export type Client = {
  id: number;
  cnpj: string;
  razao_social: string;
  nome_fantasia: string;
  email: string;
  cep: string;
  endereco: string;
  numero: string;
  bairro: string;
  cidade_id: number;
  nickname: string;
};

export function getAccessGroupClients(
  accessGroupId: number,
  callback: (clients: Client[]) => void,
  handleError: (errorMsg: string) => void
): void {
  api
    .get<{ data: Client[] }>(`/access_groups/${accessGroupId}/clients`)
    .then(result => {
      const { data } = result;
      callback(data || []);
    })
    .catch((error: AxiosError<objError>) => {
      const errorData = error?.response?.data;
      handleError(errorData?.message || DEFAULT_RESQUEST_ERROR_MESSAGE);
    });
}

export function getAvailableClients(
  accessGroupId: number,
  callback: (clients: Client[]) => void,
  handleError: (errorMsg: string) => void
): void {
  api
    .get<{ data: Client[] }>(`/access_groups/${accessGroupId}/available-clients`)
    .then(result => {
      const { data } = result;
      callback(data || []);
    })
    .catch((error: AxiosError<objError>) => {
      const errorData = error?.response?.data;
      handleError(errorData?.message || DEFAULT_RESQUEST_ERROR_MESSAGE);
    });
}

export function addClientToAccessGroup(
  accessGroupId: number,
  clientId: number,
  callback: () => void,
  handleError: (errorMsg: string) => void
): void {
  api
    .post(`/access_groups/${accessGroupId}/clients`, { client_id: clientId })
    .then(() => callback())
    .catch((error: AxiosError<objError>) => {
      const errorData = error?.response?.data;
      handleError(errorData?.message || DEFAULT_RESQUEST_ERROR_MESSAGE);
    });
}

export function removeClientFromAccessGroup(
  accessGroupId: number,
  clientId: number,
  callback: () => void,
  handleError: (errorMsg: string) => void
): void {
  api
    .delete(`/access_groups/${accessGroupId}/clients/${clientId}`)
    .then(() => callback())
    .catch((error: AxiosError<objError>) => {
      const errorData = error?.response?.data;
      handleError(errorData?.message || DEFAULT_RESQUEST_ERROR_MESSAGE);
    });
}

/**
 * Generate a fake email in the format: <cnpj>@<nome-fantasia>.com.br
 * where cnpj is numbers only and nome_fantasia is lowercase letters only
 */
function generateFakeEmail(cnpj: string, nomeFantasia: string): string {
  // Remove non-numeric characters from CNPJ
  const cleanCnpj = cnpj.replace(/\D/g, '');

  // Remove non-letter characters from nome_fantasia and convert to lowercase
  const cleanNomeFantasia = nomeFantasia.replace(/[^a-zA-Z]/g, '').toLowerCase();

  return `${cleanCnpj}@${cleanNomeFantasia}.com.br`;
}

export function updateClientNickname(
  clientId: number,
  nickname: string,
  client: Client,
  callback: () => void,
  handleError: (errorMsg: string) => void
): void {
  // Generate fake email to pass validation
  const fakeEmail = generateFakeEmail(client.cnpj, client.nome_fantasia);

  api
    .patch(`/clients/${clientId}`, {
      cnpj: client.cnpj,
      razao_social: client.razao_social,
      nome_fantasia: client.nome_fantasia,
      email: fakeEmail,
      cep: client.cep,
      endereco: client.endereco,
      numero: client.numero,
      bairro: client.bairro,
      cidade_id: client.cidade_id,
      nickname
    })
    .then(() => callback())
    .catch((error: AxiosError<objError>) => {
      const errorData = error?.response?.data;
      handleError(errorData?.message || DEFAULT_RESQUEST_ERROR_MESSAGE);
    });
}
